import { IBookingCartPreviewCartItem } from "@/api_framework/api_modals/Bookings";
import previous from "@/assets/previous.svg";

interface IDiagnosticsSectionProps {
  isLoading: boolean;
  diagnostics: IBookingCartPreviewCartItem[];
  formatAmount: (amount: number) => string;
}

const skeletonClass = "animate-pulse rounded bg-[#E9ECF2]";

// Diagnostics are attached to the booking by the lab/EMR flows, so VMS shows them read-only —
// there is no diagnostics catalog to add from and removing one has to happen where it was added.
const DiagnosticsSection: React.FC<IDiagnosticsSectionProps> = ({ isLoading, diagnostics, formatAmount }) => {
  if (!isLoading && diagnostics.length === 0) {
    return null;
  }

  return (
    <section className="px-4">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-[14px] font-semibold text-[#1F2431]">Diagnostics</h4>
      </div>
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 2 }).map((_, index) => (
            <div key={index} className="rounded-[10px] border border-[#E2E4EA] p-3">
              <div className={`${skeletonClass} h-5 w-2/3`} />
              <div className={`${skeletonClass} mt-2 h-4 w-1/3`} />
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-[10px] border border-[#E2E4EA]">
          {diagnostics.map((diagnostic, index) => {
            const finalPrice = Number(diagnostic.final_price ?? diagnostic.price ?? 0);
            const originalPrice = Number(diagnostic.price ?? finalPrice);
            const discountPercent = Number(diagnostic.discount_percent ?? 0);
            const isBilled = String(diagnostic.billing_status ?? "").toUpperCase() === "BILLED";
            const isLastItem = index === diagnostics.length - 1;

            return (
              <div
                key={diagnostic.id}
                className={`flex items-center justify-between gap-2 p-[10px] ${isLastItem ? "" : "border-b border-dashed border-[#EAEAEB]"}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-[14px] font-medium text-[#1F2431]">{diagnostic.name}</p>
                    {diagnostic.previously_added && (
                      <span
                        className="shrink-0 inline-flex items-center gap-1 rounded-[44px] px-2 py-0.5 text-[10px] font-medium text-[#4375FF]"
                        style={{
                          background: "linear-gradient(111.32deg, #F2F4FF 29.47%, #D0DDFF 106.2%)",
                          border: "0.5px solid #8CA9FA",
                        }}
                      >
                        <img src={previous} alt="previous" className="h-3 w-3" />
                        Previously added
                      </span>
                    )}
                    {isBilled && (
                      <span className="shrink-0 rounded-[44px] bg-[#EFFAF1] px-2 py-0.5 text-[10px] font-medium text-[#079A27]">
                        Billed
                      </span>
                    )}
                  </div>
                  {diagnostic.description && (
                    <p className="mt-1 line-clamp-2 text-[12px] text-[#6D6D6D]">{diagnostic.description}</p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2 text-[14px]">
                  {discountPercent > 0 && (
                    <>
                      <span className="text-[12px] font-medium text-[#239A3D]">{`${discountPercent}%`}</span>
                      {originalPrice > finalPrice && (
                        <span className="text-[12px] font-medium text-[#8A92A2] line-through">
                          {formatAmount(originalPrice)}
                        </span>
                      )}
                    </>
                  )}
                  <span className="font-medium text-[#1F2431]">
                    {finalPrice === 0 ? "Free" : formatAmount(finalPrice)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default DiagnosticsSection;
